/**
 * Narrative Director (Part B §3): a seeded, deterministic scheduler owned by the Market (free play + rounds).
 *  - chatter post every 2-6 s; mid-tier viral image post every 30-90 s (virality 0.2-0.5);
 *    major catalyst (SIM Elon/Trump + image) every 2-5 min (virality 0.6-1.0).
 *  - each viral post floods New Pairs: sniper deploys (0.15-1.2 s), a Hawkes main wave (1-8 s), a long tail (8-45 s).
 *  - exactly one canonical coin (post image, key-noun ticker, fresh dev) per narrative; copies mostly die instantly,
 *    10-20% are mini-runners, and one vamp with a clean image may appear 10-40 s later.
 * Everything is emitted through the Market's scheduled event queue, so it is replayable and ordered.
 */
import { SIM, type FeeMode, type Pair, type Platform } from './config';
import { ACCOUNTS, MEME_PAGES, SPECIAL } from './content';
import { isMajorCatalyst } from './catalyst';
import { copyArt, noiseArt } from './artmix';
import { CATALYST_REPLIES, CHATTER, pickNarrative, riffName, type Narrative } from './narratives';
import { kolPlan } from './scenario';
import { KOLS_ALL } from './kols';
import { fakeAddr, Rng } from './rng';
import type { Art, ScenarioEvent, TokenSpec, Tweet } from './types';

export interface Viral { id: string; t: number; virality: number; major: boolean; key: string; narrative: string; canonicalId: string; coins: number }

export const DIRECTOR = {
  chatterS: [2, 6] as const, midS: [30, 90] as const, majorS: [120, 300] as const,
  flood: (v: number): [number, number] => (v < 0.4 ? [5, 15] : v < 0.7 ? [15, 45] : [45, 110]),
  windowS: (v: number) => (v < 0.4 ? 20 : v < 0.7 ? 30 : 45),
  runMul: (v: number): [number, number] => (v < 0.4 ? [3, 8] : v < 0.7 ? [8, 40] : [40, 300]),
};

type Sink = (events: ScenarioEvent[]) => void;
const PADS: [Platform, number][] = [['pump', 74], ['bonk', 12], ['bags', 6], ['moonshot', 3], ['believe', 2], ['jupstudio', 3]];

export class Director {
  private r: Rng; private nextChatter: number; private nextMid: number; private nextMajor: number; private n = 0;
  readonly virals: Viral[] = [];
  enabled = true;
  constructor(seed: number, now: number, private sink: Sink) {
    this.r = new Rng(seed).derive('director');
    this.nextChatter = now + this.r.range(1000, 3000);
    this.nextMid = now + this.r.range(...DIRECTOR.midS) * 1000 * 0.4;
    this.nextMajor = now + this.r.range(...DIRECTOR.majorS) * 1000 * 0.35;
  }
  /** Called once per market tick (deterministic, advances only its own stream). */
  step(t: number) {
    if (!this.enabled) return;
    if (t >= this.nextChatter) { this.chatter(t); this.nextChatter = t + this.r.range(...DIRECTOR.chatterS) * 1000; }
    if (t >= this.nextMid) { this.viral(t, false); this.nextMid = t + this.r.range(...DIRECTOR.midS) * 1000; }
    if (t >= this.nextMajor) { this.viral(t, true); this.nextMajor = t + this.r.range(...DIRECTOR.majorS) * 1000; }
  }
  /** Debug: force a major catalyst now. */
  trigger(t: number) { this.viral(t, true); }

  private chatter(t: number) {
    const r = this.r; const recent = this.virals.filter((v) => t - v.t < 120_000).at(-1);
    const id = `dc-${this.n++}`;
    let tw: Tweet;
    if (recent && r.chance(0.4)) {
      const text = r.pick(CATALYST_REPLIES).replace('{k}', recent.key.toLowerCase()).replace('{mc}', r.pick(['200k', '450k', '1m', '80k', '2m']));
      tw = { id, t, author: r.pick(ACCOUNTS), text, kind: r.chance(0.5) ? 'reply' : 'quote', replyTo: recent.major ? 'eIonmusk_sim' : undefined, quoted: undefined };
    } else if (r.chance(0.04)) tw = { id, t, author: r.pick(SPECIAL), text: r.pick(['interesting', 'big things coming', 'working late again', 'the future is going to be wild', '!!']), kind: 'text' };
    else tw = { id, t, author: r.pick(ACCOUNTS), text: r.pick(CHATTER), kind: r.chance(0.15) ? 'reply' : 'text' };
    this.sink([{ t, kind: 'tweet', tweet: tw }]);
  }

  private viral(t: number, major: boolean) {
    const r = this.r.derive(`v${this.n++}`);
    const n: Narrative = pickNarrative(r);
    const virality = major ? r.range(0.6, 1.0) : r.range(0.2, 0.5);
    const author = major ? r.pick(SPECIAL) : r.pick(MEME_PAGES);
    const art: Art = n.arch === 'phrase' || n.arch === 'meta' ? { seed: r.int(1, 2 ** 31), kind: 'paint', tag: n.tag, text: n.key.toLowerCase() } : r.chance(0.5) ? { seed: r.int(1, 2 ** 31), kind: 'photo', photo: tilePick(r, n.tag) } : { seed: r.int(1, 2 ** 31), kind: 'paint', tag: n.tag };
    const post: Tweet = { id: `dv-${t}-${this.n}`, t, author, text: major && n.arch !== 'billionaire' && r.chance(0.3) ? n.key.toLowerCase() : n.text, image: art, kind: 'image', target: false };
    const catalyst = isMajorCatalyst(post) ? post.id : undefined;
    const ev: ScenarioEvent[] = [{ t, kind: 'tweet', tweet: post }];
    const up = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) || 'MEME';
    // a major catalyst always uses the major row (the spec's virality ranges overlap at 0.6-0.7)
    const tier = major ? 0.85 : virality;
    const [lo, hi] = DIRECTOR.flood(tier); const total = r.int(lo, hi); const win = DIRECTOR.windowS(tier) * 1000;
    const pad = r.weighted(PADS); const pair: Pair = r.weighted([['SOL', 80], ['SPACEX', 10], ['TESLA', 10]]); const fee: FeeMode = r.weighted([['none', 40], ['dividends', 40], ['feesplit', 20]]);
    const base = (id: string, at: number, patch: Partial<TokenSpec>): void => {
      const rt = r.derive(id);
      ev.push({ t: Math.round(at), kind: 'launch', token: { id, name: n.key, ticker: up(n.key), platform: pad, pair, feeMode: fee, art, address: fakeAddr(rt, rt.chance(0.7) ? 'pump' : ''), devAddress: fakeAddr(rt), devLaunches: rt.int(3, 200), devBuySol: rt.range(0.2, 2.5), role: 'copy', fate: 'weak', narrativeId: post.id, ...patch } });
    };
    // canonical: exact image, best ticker, fresh dev, 0.4-2.5 s after the post
    const cid = `nv${this.n}-c`; const canonAt = t + r.range(400, 2500);
    const [m1, m2] = DIRECTOR.runMul(tier); const runMul = r.range(m1, m2);
    base(cid, canonAt, { role: 'target', canonical: true, devLaunches: r.int(0, 2), sniperWave: true, catalyst, ...(catalyst ? { fate: 'runner', runMul } : { tier: 'hot', fate: 'weak' }) });
    if (catalyst) for (const e of kolPlan(r.derive('kol'), cid, canonAt - t, 'pro')) ev.push({ ...e, t: Math.round(t + e.t) });
    // flood: snipers (0.15-1.2 s, exact key then its ticker), Hawkes-like main wave (1-8 s), long tail (8-45 s)
    const times: number[] = [];
    const snipers = r.int(3, 10); for (let i = 0; i < snipers && times.length < total - 1; i++) times.push(t + r.range(150, 1200));
    const mainN = Math.round((total - 1 - times.length) * 0.65); let x = t + 1000; let lam = major ? 4 : 1.5;
    for (let i = 0; i < mainN && x < t + 8000; i++) { x += -Math.log(1 - r.next()) / lam * 1000; lam = Math.min(major ? 10 : 4, lam * 1.08 + 0.2); times.push(Math.min(x, t + 8000)); }
    while (times.length < total - 1) times.push(t + r.range(8000, win));
    times.sort((a, b) => a - b);
    times.forEach((at, i) => {
      const rc = r.derive('c' + i); const id = `nv${this.n}-${i}`;
      const nm = i < snipers ? (i % 2 ? { name: up(n.key), ticker: up(n.key) } : { name: n.key, ticker: up(n.key) }) : riffName(rc, n);
      const mini = rc.chance(0.15);
      base(id, at, { name: nm.name, ticker: nm.ticker, art: copyArt(rc, art, post.text, author.name, nm.ticker), platform: rc.chance(0.8) ? pad : rc.weighted(PADS), pair: rc.chance(0.85) ? pair : rc.pick(['SOL', 'SPACEX', 'TESLA'] as const), feeMode: rc.chance(0.8) ? fee : rc.pick(['none', 'dividends', 'feesplit'] as const),
        ...(mini ? { fate: rc.pick(['dump', 'rug'] as const), runMul: rc.range(2, 5) } : { tier: 'dead' }) });
      if (rc.chance(0.08)) ev.push({ t: Math.round(at + rc.range(1500, 8000)), kind: 'kol', tokenId: id, side: 'buy', kolId: rc.pick(KOLS_ALL).id, sol: rc.range(0.3, 2) });
    });
    // one vamp with a cleaner image, 10-40 s later
    if (r.chance(0.6)) base(`nv${this.n}-vamp`, t + r.range(10_000, 40_000), { role: 'vamp', art: { ...art, fx: undefined }, name: n.key, ticker: up(n.key), fate: 'grind', runMul: r.range(2, 10), catalyst, tier: catalyst ? undefined : 'slowcook' });
    this.virals.push({ id: post.id, t, virality, major, key: n.key, narrative: n.id, canonicalId: cid, coins: total });
    if (this.virals.length > 50) this.virals.shift();
    this.sink(ev.sort((a, b) => a.t - b.t));
    void SIM;
  }
}
function tilePick(r: Rng, tag: string): string { return (TAG_TILES[tag] ?? ['sad-blob', 'traffic-cone', 'plastic-chair', 'rubber-duck', 'egg-keyboard'])[r.int(0, (TAG_TILES[tag]?.length ?? 5) - 1)]!; }
const TAG_TILES: Record<string, string[]> = { frog: ['frog-meh', 'frog-wallet'], dog: ['scruffy-dog', 'sweater-dog'], 'dog-costume': ['sweater-dog'], cat: ['grumpy-cat'], capybara: ['capybara'], robot: ['doodle-robot'], raccoon: ['bin-raccoon'], chair: ['plastic-chair'], rocket: ['paper-rocket'], mushroom: ['wild-mushroom'], cone: ['traffic-cone'], egg: ['egg-keyboard'], 'duck-toy': ['rubber-duck'], duck: ['rubber-duck'], mascot: ['sad-blob'] };
export { noiseArt };

/** Feed age is measured on the market clock (never the wall clock). */
export const postAge = (marketTime: number, postT: number) => Math.max(0, marketTime - postT);
